import { useState, useEffect } from 'react';
import { colmenasAPI, zonasAPI, avisosAPI } from '../api/client';
import api from '../api/client';
import { useTranslation } from 'react-i18next';

// Catálogo de sustancias (mismos códigos que en Bitácora; el backend decide apto_bio).
const SUSTANCIAS = [
  { grupo: 'organicos', items: ['ameisensaeure', 'oxalsaeure_traeufeln', 'oxalsaeure_sublimation', 'milchsaeure', 'thymol'] },
  { grupo: 'biotecnicos', items: ['drohnenbrutentnahme', 'bannwaben', 'kunstschwarm'] },
  { grupo: 'sinteticos', items: ['amitraz', 'flumethrin', 'coumaphos'] },
  { grupo: 'otroGrupo', items: ['otro'] },
];

export default function AccionesLote() {
  const { t } = useTranslation();

  const ACCIONES = [
    { value: 'crear_tarea',      label: t('lote.actionTask'),   icon: '📋' },
    { value: 'registrar_evento', label: t('lote.actionEvent'),  icon: '📓' },
    { value: 'actualizar_estado',label: t('lote.actionStatus'), icon: '✓'  },
  ];

  const ESTADOS_KEYS = [
    { key: 'necesita_alimento',  label: t('lote.stateNeedFood') },
    { key: 'tratamiento_activo', label: t('lote.stateTreatment') },
    { key: 'sin_reina',          label: t('lote.stateNoQueen') },
    { key: 'revisar_pronto',     label: t('lote.stateReviewSoon') },
    { key: 'alza_puesta',        label: t('lote.stateAlza') },
    { key: 'poblacion_baja',     label: t('lote.stateLowPop') },
  ];

  const [colmenas, setColmenas] = useState([]);
  const [seleccionadas, setSeleccionadas] = useState(new Set());
  const [accion, setAccion] = useState('crear_tarea');
  const [form, setForm] = useState({
    descripcion: '', prioridad: 'normal', tipo: 'inspeccion',
    titulo: '', key: 'necesita_alimento', valor: true,
    fecha: new Date().toISOString().slice(0, 10),
    fecha_limite: '',
  });
  const [sustancia, setSustancia] = useState('');
  const [sustanciaOtro, setSustanciaOtro] = useState('');
  const [avisos, setAvisos] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [ejecutando, setEjecutando] = useState(false);
  const [filtroZona, setFiltroZona] = useState('');
  const [zonas, setZonas] = useState([]);

  useEffect(() => {
    Promise.all([colmenasAPI.list(), zonasAPI.list()]).then(([c, z]) => {
      setColmenas(c.data.filter(col => col.activa));
      setZonas(z.data);
    });
  }, []);

  const toggleColmena = (id) => {
    setSeleccionadas(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // Avisos de carencia (no bloqueantes): al registrar tratamiento en lote,
  // listar las colmenas seleccionadas que tienen alza colocada sin retirar.
  const esTratamientoLote = accion === 'registrar_evento' && form.tipo === 'tratamiento';
  const seleccionadasKey = [...seleccionadas].sort((a, b) => a - b).join(',');
  useEffect(() => {
    if (!esTratamientoLote || seleccionadas.size === 0) { setAvisos([]); return; }
    let cancelado = false;
    const fecha = form.fecha ? new Date(form.fecha + 'T12:00:00').toISOString() : null;
    Promise.all(
      [...seleccionadas].map(id =>
        avisosAPI.carencia(id, { contexto: 'tratamiento', ...(fecha ? { fecha } : {}) })
          .then(r => r.data).catch(() => null)
      )
    ).then(res => {
      if (!cancelado) setAvisos(res.filter(a => a && a.alza_activa));
    });
    return () => { cancelado = true; };
  }, [esTratamientoLote, seleccionadasKey, form.fecha]); // eslint-disable-line

  const colmenasFiltradas = filtroZona
    ? colmenas.filter(c => c.zona_id === parseInt(filtroZona))
    : colmenas;

  const seleccionarTodas = () => {
    const filtradas = colmenasFiltradas.map(c => c.id);
    setSeleccionadas(new Set(filtradas));
  };

  const ejecutar = async () => {
    if (seleccionadas.size === 0) return;
    setEjecutando(true); setResultado(null);
    try {
      const datos = accion === 'crear_tarea'
        ? { descripcion: form.descripcion, prioridad: form.prioridad, fecha_limite: form.fecha_limite ? new Date(form.fecha_limite + 'T12:00:00').toISOString() : null }
        : accion === 'registrar_evento'
        ? {
            tipo: form.tipo, titulo: form.titulo,
            fecha: form.fecha ? new Date(form.fecha + 'T12:00:00').toISOString() : null,
            ...(form.tipo === 'tratamiento' && sustancia ? {
              tratamiento_sustancia: sustancia,
              ...(sustancia === 'otro' && sustanciaOtro ? { tratamiento_sustancia_otro: sustanciaOtro } : {}),
            } : {}),
          }
        : { key: form.key, valor: form.valor };

      const r = await api.post('/acciones/lote', {
        colmena_ids: [...seleccionadas],
        accion,
        datos,
      });
      setResultado(r.data);
      setSeleccionadas(new Set());
    } finally { setEjecutando(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>{t('lote.title')}</h1>
          <p style={S.sub}>{t('lote.subtitle')}</p>
        </div>
      </div>

      <div className="grid-2col">
        <div style={S.panel}>
          <div style={S.panelHeader}>
            <span>{t('lote.hives', { sel: seleccionadas.size, total: colmenasFiltradas.length })}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {zonas.length > 1 && (
                <select style={S.miniSelect} value={filtroZona}
                  onChange={e => setFiltroZona(e.target.value)}>
                  <option value="">{t('common.allZones')}</option>
                  {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
                </select>
              )}
              <button onClick={seleccionarTodas} style={S.linkBtn}>{t('lote.selectAll')}</button>
              <button onClick={() => setSeleccionadas(new Set())} style={S.linkBtn}>{t('lote.selectNone')}</button>
            </div>
          </div>
          <div style={S.colmenaLista}>
            {colmenasFiltradas.map(c => (
              <label key={c.id} style={{ ...S.colmenaItem, background: seleccionadas.has(c.id) ? '#FDF0DC' : 'white' }}>
                <input type="checkbox" checked={seleccionadas.has(c.id)}
                  onChange={() => toggleColmena(c.id)} style={{ accentColor: '#E8922A' }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{c.nombre}</div>
                  <div style={{ fontSize: 12, color: '#8B6355' }}>{c.zona_nombre}</div>
                </div>
                <span style={{ marginLeft: 'auto', fontSize: 16 }}>
                  {c.semaforo === 'rojo' ? '🔴' : c.semaforo === 'amarillo' ? '🟡' : '🟢'}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div style={S.panel}>
          <div style={S.panelHeader}>{t('lote.actionTitle')}</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '16px', marginBottom: 0 }}>
            {ACCIONES.map(a => (
              <label key={a.value} style={{ ...S.accionOpt, background: accion === a.value ? '#FDF0DC' : 'white', borderColor: accion === a.value ? '#E8922A' : '#FAE0B0' }}>
                <input type="radio" name="accion" value={a.value} checked={accion === a.value}
                  onChange={() => setAccion(a.value)} style={{ accentColor: '#E8922A' }} />
                <span style={{ fontSize: 18 }}>{a.icon}</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{a.label}</span>
              </label>
            ))}
          </div>

          <div style={{ padding: '0 16px 16px' }}>
            {accion === 'crear_tarea' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input value={form.descripcion} onChange={e => set('descripcion', e.target.value)}
                  className="form-input" placeholder={t('lote.descPlaceholder')} />
                <select value={form.prioridad} onChange={e => set('prioridad', e.target.value)} className="form-input">
                  <option value="baja">{t('lote.priorityLow')}</option>
                  <option value="normal">{t('lote.priorityNormal')}</option>
                  <option value="alta">{t('lote.priorityHigh')}</option>
                </select>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>{t('lote.dueDateLabel')}</label>
                  <input type="date" value={form.fecha_limite} onChange={e => set('fecha_limite', e.target.value)}
                    className="form-input" />
                </div>
              </div>
            )}

            {accion === 'registrar_evento' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <select value={form.tipo} onChange={e => set('tipo', e.target.value)} className="form-input">
                  <option value="inspeccion">{t('lote.tipoInspeccion')}</option>
                  <option value="alimentacion">{t('lote.tipoAlimentacion')}</option>
                  <option value="tratamiento">{t('lote.tipoTratamiento')}</option>
                  <option value="cosecha">{t('lote.tipoCosecha')}</option>
                  <option value="nota">{t('lote.tipoNota')}</option>
                </select>
                <input value={form.titulo} onChange={e => set('titulo', e.target.value)}
                  className="form-input" placeholder={t('lote.eventTitlePlaceholder')} />
                {form.tipo === 'tratamiento' && (
                  <>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>{t('tratamiento.sustanciaLabel')}</label>
                      <select className="form-input" value={sustancia} onChange={e => setSustancia(e.target.value)}>
                        <option value="">{t('tratamiento.sustanciaPlaceholder')}</option>
                        {SUSTANCIAS.map(g => (
                          <optgroup key={g.grupo} label={t(`tratamiento.grupo_${g.grupo}`)}>
                            {g.items.map(s => (
                              <option key={s} value={s}>{t(`tratamiento.sust_${s}`)}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    {sustancia === 'otro' && (
                      <input className="form-input" value={sustanciaOtro}
                        onChange={e => setSustanciaOtro(e.target.value)}
                        placeholder={t('tratamiento.sustanciaOtroPlaceholder')} />
                    )}
                  </>
                )}
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>{t('lote.eventDateLabel')}</label>
                  <input type="date" value={form.fecha} onChange={e => set('fecha', e.target.value)}
                    className="form-input" />
                </div>
                {avisos.length > 0 && (
                  <div style={S.avisoBox}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>⚠️ {t('lote.avisoAlzaTitle')}</div>
                    {avisos.map(a => {
                      const c = colmenas.find(x => x.id === a.colmena_id);
                      return (
                        <div key={a.colmena_id} style={{ fontSize: 12, marginTop: 2 }}>
                          <strong>{c ? c.nombre : `#${a.colmena_id}`}:</strong>{' '}
                          {t('avisos.tratamientoAlza', { fecha: new Date(a.alza_activa.desde).toLocaleDateString() })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {accion === 'actualizar_estado' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <select value={form.key} onChange={e => set('key', e.target.value)} className="form-input">
                  {ESTADOS_KEYS.map(ek => <option key={ek.key} value={ek.key}>{ek.label}</option>)}
                </select>
                <select value={form.valor} onChange={e => set('valor', e.target.value === 'true')} className="form-input">
                  <option value="true">{t('lote.markActive')}</option>
                  <option value="false">{t('lote.markInactive')}</option>
                </select>
              </div>
            )}

            <button onClick={ejecutar} disabled={ejecutando || seleccionadas.size === 0}
              style={{ ...S.ejecutarBtn, opacity: seleccionadas.size === 0 ? 0.5 : 1, marginTop: 20 }}>
              {ejecutando ? t('lote.running') : t('lote.applyBtn', { count: seleccionadas.size, s: seleccionadas.size !== 1 ? 's' : '' })}
            </button>

            {resultado && (
              <div style={{ ...S.resultadoBox, background: resultado.fallidos > 0 ? '#FDF0DC' : '#E8F5EC' }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  {t('lote.resultSummary', { ok: resultado.exitosos, fail: resultado.fallidos })}
                </div>
                {resultado.mensajes.slice(0, 5).map((m, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#5C3D2E' }}>{m}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  header: { marginBottom: 24 },
  title: { fontSize: 26, fontFamily: "'DM Sans', sans-serif" },
  sub: { color: '#8B6355', fontSize: 14, marginTop: 4 },
  panel: { background: 'white', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px rgba(44,26,14,0.08)', border: '1px solid rgba(232,146,42,0.12)' },
  panelHeader: { padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#8B6355', background: '#FEFCF8', borderBottom: '1px solid #F0EDE9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  colmenaLista: { maxHeight: 400, overflowY: 'auto' },
  colmenaItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #F8F4EF', transition: 'background 0.1s' },
  accionOpt: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, border: '1.5px solid', cursor: 'pointer', transition: 'all 0.15s' },
  ejecutarBtn: { width: '100%', padding: '14px', background: '#E8922A', color: 'white', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  resultadoBox: { marginTop: 14, padding: '12px 14px', borderRadius: 10 },
  avisoBox: { padding: '10px 12px', borderRadius: 10, background: '#FDF0DC', border: '1px solid #E8922A', color: '#5C3D2E', fontSize: 13 },
  miniSelect: { fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid #FAE0B0', background: 'white' },
  linkBtn: { fontSize: 12, color: '#E8922A', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' },
};
